import { Controller } from "@hotwired/stimulus"

const SVG = "http://www.w3.org/2000/svg"
// Everything inside the chart — boxes, cross-references, the answers on the arrows — is
// set at 14px, so a chart scaled to fit its column keeps its text at 12px or more. One
// that would need more scrolls sideways instead.
const MIN_SCALE = 12 / 14
const BOX_WIDTHS = [176, 160, 144, 128]
let charts = 0

// Lays a redrawn algorithm out top to bottom with dagre and draws its arrows. The boxes
// are the server's HTML, measured as they render, at the widest size that lets the chart
// fit its column (see draw); a wider one scrolls inside the figure.
// Until the layout loads, the boxes stand as a plain list; if it cannot load, the chart is
// hidden, since boxes without arrows cannot be followed, and the walk-through above
// still can. It is laid out again whenever the width it has changes — a resize, the page
// losing its scrollbar, a figure that was hidden being shown — and only then: a phone's
// URL bar coming and going resizes the window but not the column.
export default class extends Controller {
  static targets = ["frame", "canvas", "list", "node", "label", "edges", "viewport", "scrollHint"]
  static values = { layoutUrl: String, edges: Array }

  async connect() {
    this.markerId = `figure-arrow-${++charts}`
    try {
      [this.dagre] = await Promise.all([import(this.layoutUrlValue), document.fonts?.ready])
    } catch (error) {
      console.error("figure layout did not load", error)
      this.element.hidden = true
      return
    }
    if (!this.element.isConnected) return

    this.laidOutFor = null
    this.observer = new ResizeObserver(() => {
      cancelAnimationFrame(this.frameRequest)
      this.frameRequest = requestAnimationFrame(() => this.relayout())
    })
    this.observer.observe(this.viewportTarget)
  }

  disconnect() {
    this.observer?.disconnect()
    cancelAnimationFrame(this.frameRequest)
  }

  relayout() {
    const room = this.room()
    if (room <= 0 || room === this.laidOutFor) return

    this.laidOutFor = room
    this.draw(room)
    this.updateHint()
  }

  // The widest boxes that let the chart fit its column; failing that, the narrowest,
  // scaled down while the text stays readable; failing that too, as on a phone, the widest
  // again, since the chart will scroll anyway and wide boxes read best.
  draw(room) {
    let chart
    for (const width of BOX_WIDTHS) {
      chart = this.layoutAt(width)
      if (chart.width <= room) break
    }
    if (room / chart.width < MIN_SCALE) chart = this.layoutAt(BOX_WIDTHS[0])

    const { graph, width, height, shift } = chart
    Object.assign(this.canvasTarget.style, { width: `${width}px`, height: `${height}px` })
    this.listTarget.classList.remove("space-y-3")
    this.nodeTargets.forEach((node) => {
      const box = graph.node(node.dataset.id)
      Object.assign(node.style, {
        position: "absolute",
        left: `${box.x - box.width / 2 + shift.x}px`,
        top: `${box.y - box.height / 2 + shift.y}px`
      })
    })
    this.drawEdges(graph, width, height, shift)
    this.fit(chart, room)
  }

  // Widths are all written before any box is measured, so the browser reflows once per
  // width tried rather than once per box.
  layoutAt(boxWidth) {
    const { Graph, layout } = this.dagre
    this.nodeTargets.forEach((node) => { node.style.width = `${boxWidth}px` })
    const sizes = this.nodeTargets.map((node) => [node.dataset.id, node.offsetWidth, node.offsetHeight])

    const graph = new Graph({ multigraph: true })
    graph.setGraph({ rankdir: "TB", nodesep: 14, ranksep: 32, edgesep: 10, marginx: 2, marginy: 2 })
    graph.setDefaultEdgeLabel(() => ({}))
    sizes.forEach(([id, width, height]) => graph.setNode(id, { width, height }))
    this.edgesValue.forEach((edge, index) => {
      const label = this.labelFor(index)
      const size = label ? { width: label.offsetWidth + 8, height: label.offsetHeight + 4, labelpos: "c" } : {}
      graph.setEdge(edge.from, edge.to, size, String(index))
    })
    layout(graph)
    return this.bounds(graph)
  }

  // dagre sizes the graph by its boxes and labelled arrows only; an unlabelled arrow that
  // skips ranks or turns back can bend outside that. The chart is sized to everything
  // drawn, so nothing is clipped and a chart is never taken to fit when it does not.
  bounds(graph) {
    const margin = 8
    const xs = []
    const ys = []
    graph.nodes().forEach((id) => {
      const { x, y, width, height } = graph.node(id)
      xs.push(x - width / 2, x + width / 2)
      ys.push(y - height / 2, y + height / 2)
    })
    graph.edges().forEach((key) => {
      const edge = graph.edge(key)
      edge.points.forEach((point) => { xs.push(point.x); ys.push(point.y) })
      if (edge.width) {
        xs.push(edge.x - edge.width / 2, edge.x + edge.width / 2)
        ys.push(edge.y - edge.height / 2, edge.y + edge.height / 2)
      }
    })
    const left = Math.min(...xs) - margin
    const top = Math.min(...ys) - margin
    return {
      graph,
      width: Math.max(...xs) + margin - left,
      height: Math.max(...ys) + margin - top,
      shift: { x: -left, y: -top }
    }
  }

  room() {
    const style = getComputedStyle(this.viewportTarget)
    return this.viewportTarget.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
  }

  fit({ graph, width, height, shift }, room) {
    const scale = width > room && room / width >= MIN_SCALE ? room / width : 1
    this.canvasTarget.style.transform = scale < 1 ? `scale(${scale})` : ""
    this.canvasTarget.style.transformOrigin = "top left"
    Object.assign(this.frameTarget.style, { width: `${width * scale}px`, height: `${height * scale}px` })

    // A chart that still does not fit opens on its first box, not on its left edge.
    const start = graph.node(this.nodeTargets[0].dataset.id)
    const padding = parseFloat(getComputedStyle(this.viewportTarget).paddingLeft)
    this.viewportTarget.scrollLeft = padding + (start.x + shift.x) * scale - this.viewportTarget.clientWidth / 2
  }

  drawEdges(graph, width, height, shift) {
    const svg = this.edgesTarget
    svg.setAttribute("width", width)
    svg.setAttribute("height", height)
    svg.replaceChildren(this.marker(this.markerId))

    this.edgesValue.forEach((edge, index) => {
      const route = graph.edge(edge.from, edge.to, String(index))
      const path = document.createElementNS(SVG, "path")
      const points = route.points.map((point, i) => `${i ? "L" : "M"}${point.x + shift.x} ${point.y + shift.y}`)
      path.setAttribute("d", points.join(" "))
      path.setAttribute("fill", "none")
      path.setAttribute("stroke-width", "1.5")
      path.setAttribute("class", "stroke-ink-faint")
      path.setAttribute("marker-end", `url(#${this.markerId})`)
      svg.append(path)

      const label = this.labelFor(index)
      if (!label) return
      label.style.left = `${route.x + shift.x - label.offsetWidth / 2}px`
      label.style.top = `${route.y + shift.y - label.offsetHeight / 2}px`
      label.classList.remove("invisible")
    })
  }

  marker(id) {
    const marker = document.createElementNS(SVG, "marker")
    Object.entries({ id, viewBox: "0 0 10 10", refX: "9", refY: "5", markerWidth: "7", markerHeight: "7", orient: "auto" })
      .forEach(([name, value]) => marker.setAttribute(name, value))
    const tip = document.createElementNS(SVG, "path")
    tip.setAttribute("d", "M0 0 L10 5 L0 10 z")
    tip.setAttribute("class", "fill-ink-faint")
    marker.append(tip)
    return marker
  }

  labelFor(index) {
    return this.labelTargets.find((label) => label.dataset.index === String(index))
  }

  updateHint() {
    this.scrollHintTarget.hidden = this.viewportTarget.scrollWidth <= this.viewportTarget.clientWidth
  }
}
